import dayjs from 'dayjs';
import { TooltipItem } from 'chart.js';
import { CompletedCyclesSeries } from '@/api/dashboard/types';
import { QueryDateRange } from '@/features/dashboard/types';
import {
	HOURS_FORMAT,
	DAY_MONTH_FORMAT,
	DAY_NAME_FORMAT,
	DAY_RANGE_LIMITER,
	MONTH_LONG_FORMAT,
	MONTH_SHORT_FORMAT,
	WEEK_RANGE_LIMITER,
} from './constants';

interface LineDataPoint {
	x: string;
	y: number;
	xFullDate: dayjs.Dayjs;
}

export type CompletedCyclesDataset = {
	name: string;
	datapoints: Array<LineDataPoint>;
};

export interface LabelInfo {
	text: string;
	value: dayjs.Dayjs;
}

export const formatHoursLabel = (date: dayjs.Dayjs): string => date.format(HOURS_FORMAT);

export const formatDayMonthLabel = (date: dayjs.Dayjs): string => date.format(DAY_MONTH_FORMAT);

export const formatDayNameLabel = (date: dayjs.Dayjs): string => date.format(DAY_NAME_FORMAT);

export const formatMonthLabel = (
	date: dayjs.Dayjs,
	start: dayjs.Dayjs,
	end: dayjs.Dayjs
): string => {
	const spansThroughYears = end.year() !== start.year();
	const format = spansThroughYears ? MONTH_LONG_FORMAT : MONTH_SHORT_FORMAT;

	return date.format(format);
};

export const generateOneDayRangeLabels = (start: dayjs.Dayjs, end: dayjs.Dayjs): LabelInfo[] => {
	const labels: LabelInfo[] = [];
	let currentDate = start.startOf('day');
	const endDate = end.endOf('day');

	while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
		labels.push({ text: formatHoursLabel(currentDate), value: currentDate });

		currentDate = currentDate.add(1, 'hour');
	}

	return labels;
};

export const generateDaysRangeLabels = (start: dayjs.Dayjs, end: dayjs.Dayjs): LabelInfo[] => {
	const labels: LabelInfo[] = [];
	let currentDate = start;

	const dayDiff = end.diff(start, 'day');
	const formatFn: (date: dayjs.Dayjs) => string =
		dayDiff === 6 ? formatDayNameLabel : formatDayMonthLabel;

	while (currentDate.isBefore(end) || currentDate.isSame(end)) {
		labels.push({ text: formatFn(currentDate), value: currentDate });
		currentDate = currentDate.add(1, 'day');
	}

	return labels;
};

export const generateWeekRangeLabels = (start: dayjs.Dayjs, end: dayjs.Dayjs): LabelInfo[] => {
	const labels: LabelInfo[] = [];
	let currentDate = start.startOf('week');

	while (currentDate.isBefore(end) || currentDate.isSame(end)) {
		labels.push({ text: formatDayMonthLabel(currentDate), value: currentDate });

		currentDate = currentDate.add(1, 'week');
	}

	return labels;
};

export const generateMonthsRangeLabels = (start: dayjs.Dayjs, end: dayjs.Dayjs): LabelInfo[] => {
	const labels: LabelInfo[] = [];
	let currentDate = start;

	while (currentDate.isBefore(end) || currentDate.isSame(end)) {
		labels.push({ text: formatMonthLabel(currentDate, start, end), value: currentDate });
		currentDate = currentDate.add(1, 'month');
	}

	return labels;
};

export const transformSeriesDataset = (
	formatFn: (date: dayjs.Dayjs, start: dayjs.Dayjs, end: dayjs.Dayjs) => string,
	start: dayjs.Dayjs,
	end: dayjs.Dayjs,
	series: CompletedCyclesSeries
): CompletedCyclesDataset => ({
	name: series.site?.name ?? '',
	datapoints: series.points.map((log) => ({
		x: formatFn(dayjs(log.date), start, end),
		y: Number(log.count),
		xFullDate: dayjs(log.date),
	})),
});

export const normalizeDataset = (
	labels: LabelInfo[],
	dataset: CompletedCyclesDataset
): CompletedCyclesDataset => ({
	name: dataset.name,
	datapoints: labels.map(({ text: label, value }) => {
		const datesetLog = dataset.datapoints?.find((datesetLog) => datesetLog.x === label);
		return datesetLog || { x: label, y: 0, xFullDate: value };
	}),
});

export const formatTooltipTitle = (
	tooltipItems: TooltipItem<'line'>[],
	dateRange?: QueryDateRange
): string | void => {
	const { start, end } = dateRange || {};
	if (!start || !end) return undefined;

	const dayDiff = dayjs(end).diff(dayjs(start), 'd');
	if (dayDiff < DAY_RANGE_LIMITER || dayDiff > WEEK_RANGE_LIMITER) return undefined;

	const [{ label, raw }] = tooltipItems;
	const { xFullDate } = raw as LineDataPoint;

	if (!xFullDate) return undefined;

	return `${label} - ${dayjs(xFullDate).add(6, 'd').format(DAY_MONTH_FORMAT)}`;
};
